import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Suspense, useMemo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { DashboardHeader } from '../header/DashboardHeader.jsx';
import { DashboardSidebar } from "../sidebar/DashboardSidebar.jsx";
import { HeaderActionsSlotProvider } from '../header/HeaderActionsSlot.jsx';
import { SidebarSwitcherSlotProvider } from '../sidebar/SidebarSwitcherSlot.jsx';
import { NavTreeContext } from '../sidebar/NavTreeContext.js';
import { useScopedNavLinks } from '../sidebar/useScopedNavLinks.jsx';
import LoadingPage from "../../pages/error-pages/LoadingPage.jsx";
import ChunkErrorBoundary from '../../components/ChunkErrorBoundary.jsx';
import { userDashboardLinks } from '../../routes/navConfig.jsx';
import { scopeForPath } from '../../routes/navScopes.jsx';
import { isOnboardingPath } from '../../routes/onboardingSteps.js';
import { useNavScope } from '../sidebar/useNavScope.js';
import { getSoftLandingPath } from '../../utils/rbacUtils.js';
import { getUser } from '../../services/UserProfileService.jsx';
import { UserAuth } from '../../authentication/AuthContext.jsx';
import useWorkspaceBootstrap from './useWorkspaceBootstrap.js';
import WorkspaceLoadError from './WorkspaceLoadError.jsx';
import PlaygroundBanner from '../../playground/PlaygroundBanner.jsx';
import { usePlayground } from '../../playground/PlaygroundContext.jsx';
import OnboardingBanner from '../../components/onboarding-banner/OnboardingBanner.jsx';
import AccessHoldBanner from '../../components/access/AccessHoldBanner.jsx';
import useAccessRefresh from '../../authentication/useAccessRefresh.js';
import { isHeld, releaseHoldIfLeft, reportHeldDenial } from '../../authentication/accessHold.js';
import useToast from '../../components/alert/useToast.jsx';
import '../../styles/Content.css'

// Labels to hide from sidebar when in playground mode
const PLAYGROUND_HIDDEN_LABELS = ['AI Agent', 'Developer Tools'];

const DashboardLayout = ({ dashboardType }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { isPlaygroundMode } = usePlayground();
    const { user } = UserAuth();
    const isSuperUser = useSelector(state => state.userDetails.superUser);
    const userDetailsLoaded = useSelector(state => state.userDetails.userDetailsLoaded);
    const userRole = useSelector(state => state.dashboardContext.userRole);
    const effectivePermissions = useSelector(state => state.effectivePermissions);
    const prevPermissionsRef = useRef(effectivePermissions);

    // Access re-read in place when it changes (server signal, tab return, unexpected refusal) - decided
    // 9 October 2026. A toast says it happened; the bell says what changed.
    const { showToast, toastEl } = useToast({ duration: 4000 });
    useAccessRefresh({
        onChange: (kind, message) => showToast(kind === 'reduced' ? 'warning' : 'success', message),
    });
    // A hold lasts until the user leaves the page it was put on - another page, or another org on the same page.
    const selectedOrgId = useSelector(state => state.dashboardContext.selectedOrgId);
    useEffect(() => {
        releaseHoldIfLeft(location.pathname, selectedOrgId ?? null);
    }, [location.pathname, selectedOrgId]);

    // Resume routing — always fires when the layout mounts. Login.jsx has the
    // same logic; this covers any other entry path (refresh, direct URL, etc.).
    useEffect(() => {
        if (!user?.accessToken) return;
        // Never eject a user from a wizard they're already in.
        if (location.pathname.startsWith('/onboarding') || isOnboardingPath(location.pathname)) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await getUser({ accessToken: user.accessToken });
                if (cancelled) return;
                const prog = res?.user?.globalPreferences?.onboardingProgress;
                if (!prog || !prog.stepIdx) return;
                // Stepper still in progress → resume it.
                navigate('/onboarding', { state: { resume: prog }, replace: true });
            } catch (_) { /* swallow — non-fatal */ }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.accessToken]);

    // Fetch user profile + permissions only if not already loaded (e.g. after a page refresh).
    // On normal login, permissions are fetched in Login.jsx and stored in Redux before we get here.
    // Skip only when BOTH are present. Permissions alone aren't enough: some entry paths (e.g. the
    // invitation-accept flow) set effectivePermissions without userDetails, and the render gate below
    // needs userDetailsLoaded too — guarding on permissions alone left the loader stuck forever.
    // A failed load is retried, then shown (WorkspaceLoadError) instead of leaving the content blank.
    const workspace = useWorkspaceBootstrap({
        loaded: isPlaygroundMode || (!!effectivePermissions && userDetailsLoaded),
    });

    // Prefetch settings route chunks in the background so navigation is instant
    useEffect(() => {
        const timer = setTimeout(() => {
            import('../../pages/admin/user-management/CombinedUserManagement');
            import('../../pages/admin/access-management/org-management/OrgManagement');
            import('../../pages/admin/role-access-management/role-mgmt/RoleManagement');
        }, 2000);
        return () => clearTimeout(timer);
    }, []);

    // The focused scope and its already-RBAC-filtered links. Replaces the old per-dashboardType
    // switch, which resolved to the same canonical tree on every branch.
    const scope = useNavScope();

    // Awaiting-approval landing: no top-section items, just the sidebar shell so the bottom avatar
    // popup (Profile / App Settings / Help / Logout) stays reachable.
    const isDemoShell = dashboardType === 'demo' && !isSuperUser;

    // Gate on permissions AND userDetails — `superUser` isn't persisted, so on hard refresh it's
    // briefly false while perms are loaded, flashing the role-based nav (e.g. Org Management).
    const permissionsReady = (!!effectivePermissions || isPlaygroundMode) && (isPlaygroundMode || userDetailsLoaded);

    const filteredLinks = useMemo(() => {
        if (!permissionsReady || isDemoShell) return [];
        let filtered = scope.links;
        if (isPlaygroundMode) {
            filtered = filtered.filter(link => !PLAYGROUND_HIDDEN_LABELS.includes(link.label));
        }
        return filtered;
    }, [scope, isPlaygroundMode, permissionsReady, isDemoShell]);

    // What this view's nav shows, built once: the sidebar renders it and every page header's section switcher
    // walks it, so the dropdown lists the game's (or admin's, or the portfolio's) pages rather than the hub's.
    const navTree = useScopedNavLinks(filteredLinks, scope);

    // When effective permissions change (e.g. after accepting an invitation),
    // check if the current page is still accessible. If not, redirect to the
    // first accessible page in the navbar.
    useEffect(() => {
        if (isSuperUser) return; // super users are governed by the dedicated boundary guard below
        if (!effectivePermissions || !prevPermissionsRef.current) {
            prevPermissionsRef.current = effectivePermissions;
            return;
        }
        // Only act when permissions actually changed (not on initial load)
        if (prevPermissionsRef.current === effectivePermissions) return;
        prevPermissionsRef.current = effectivePermissions;

        if (filteredLinks.length === 0) return;

        const isPathInLinks = (items, pathname) => {
            for (const item of items) {
                if (item.path && pathname.startsWith(item.path)) return true;
                if (item.subItems?.length > 0 && isPathInLinks(item.subItems, pathname)) return true;
            }
            return false;
        };

        // Pages outside the navbar links (one-time / deeplink) must not be bounced
        // off by a permissions refetch (e.g. selecting a game on the connect form).
        if (location.pathname.startsWith('/admin-settings/game-mgmt/data-connections')) return;
        // The Grant/Assign Access wizard is a deeplink form (opened via a button, not in the navbar), so
        // switching org/game/build context mid-flow refetches permissions — don't bounce the operator to
        // Home for a page they opened deliberately and that will never be in filteredLinks.
        if (location.pathname.startsWith('/admin-settings/access-mgmt/grant-access')) return;
        // Homepage and Components are ungated pages that left the nav but still open by URL, so they are never in it.
        if (['/homepage', '/components'].includes(location.pathname.replace(/\/+$/, '').toLowerCase())) return;
        // A path a scope owns is legitimate even when the CURRENT scope's links don't contain it —
        // resolving the scope is what puts it in the nav. Bouncing here would eject the user from
        // their own deep link on the permissions refetch that every game switch triggers.
        if (scopeForPath(location.pathname)) return;

        if (!isPathInLinks(filteredLinks, location.pathname)) {
            // Access changed under an open page: keep it (the banner explains) instead of navigating away.
            if (isHeld(location.pathname)) {
                reportHeldDenial(location.pathname, 'You no longer have access to this page.');
                return;
            }
            // Prefer Home (when reachable) over the first accessible link — friendlier than dropping
            // the user on Profile after switching to an org where their current page isn't accessible.
            const firstPath = getSoftLandingPath(userDashboardLinks, userRole, effectivePermissions);
            navigate(firstPath, { replace: true });
        }
    }, [effectivePermissions, filteredLinks, location.pathname, userRole, navigate]);

    const getContentClass = () => {
        return location.pathname.split('/').at(-1) === 'create-remote-config';
    }

    return (
        <HeaderActionsSlotProvider>
            <SidebarSwitcherSlotProvider>
            <NavTreeContext.Provider value={navTree}>
            <div className="main">
                <DashboardSidebar navLinks={filteredLinks} scope={scope} />
                <div className="main-container">
                    <PlaygroundBanner />
                    <DashboardHeader dashboardType={dashboardType} />
                    <AccessHoldBanner />
                    <OnboardingBanner />
                    {toastEl}
                    {/* The AI chatbot panel that used to sit beside .content is archived in
                        src/old/milestone2. The flex wrapper stays: it is what constrains
                        .content's height (see styles/Content.css), so removing it would
                        change the layout. */}
                    <div className="ai-chatbot-content-wrapper">
                        <div className={`content ${getContentClass() === true ? 'relative' : ''}`}>
                            <ChunkErrorBoundary>
                                <Suspense fallback={<LoadingPage />}>
                                    {/* Permissions bootstrap — blank content, never a spinner (C.9). Blank only
                                        while it loads: a load that failed says so and offers a retry. */}
                                    {permissionsReady
                                        ? <Outlet />
                                        : (workspace.failed ? <WorkspaceLoadError onRetry={workspace.retry} /> : null)}
                                </Suspense>
                            </ChunkErrorBoundary>
                        </div>
                    </div>
                </div>
            </div>
            </NavTreeContext.Provider>
            </SidebarSwitcherSlotProvider>
        </HeaderActionsSlotProvider>
    );
};

export default DashboardLayout;